interface Props {
  readonly title: string;
  readonly description?: string;
  readonly action?: React.ReactNode;
  readonly icon?: React.ReactNode;
  /**
   * Heading level for {@link Props.title}. Defaults to `2`, which is right when
   * the state sits inside a page that already has its own h1.
   *
   * Pass `1` when the empty state *is* the page — a route that resolved to
   * nothing still owes the reader a top-level heading, and a page whose only
   * heading is an h2 reads to a screen reader as a fragment of something else.
   */
  readonly headingLevel?: 1 | 2 | 3;
}

export function EmptyState({
  title,
  description,
  action,
  icon,
  headingLevel = 2,
}: Props) {
  const Heading = `h${headingLevel}` as const;
  return (
    <div className="mx-auto flex max-w-md flex-col items-center text-center" style={{ color: "var(--fg-muted)" }}>
      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full" style={{ backgroundColor: "var(--bg-muted)" }}>
        {icon ?? <DefaultIcon />}
      </div>
      <Heading className="text-lg font-semibold" style={{ color: "var(--fg)" }}>
        {title}
      </Heading>
      {description ? <p className="mt-1 text-sm">{description}</p> : null}
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}

function DefaultIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden style={{ color: "var(--fg-subtle)" }}>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
    </svg>
  );
}
